import { Component, EventEmitter, inject, Input, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Observable, Subject, Subscription } from 'rxjs';
import { CustomModalComponent } from 'src/app/components/custom-modal/custom-modal.component';
import { TypeMaintenanceSchedulingEnums } from 'src/app/enum/maintenance-scheduling.enums';
import { ActionColor, CheckBoxAction, TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { MaintenanceSchedulingModel } from 'src/app/models/maintenance/maintenance-scheduling.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { MaintenanceSchedulingDetailCaptureTableModel } from 'src/app/models/tables/maintenance/maintenance-scheduling-detail-capture-table.model';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { MaintenanceSchedulingDetailService } from 'src/app/services/maintenance/maintenance-scheduling-detail.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { ProcessMaintenanceSchedulingComponent } from '../process-maintenance-scheduling/process-maintenance-scheduling.component';
import { GenerateMaintenanceOrdersComponent } from '../generate-maintenance-orders/generate-maintenance-orders.component';

@Component({
  selector: 'app-maintenance-scheduling-detail',
  templateUrl: './maintenance-scheduling-detail.component.html',
  styleUrls: ['./maintenance-scheduling-detail.component.scss'],
  standalone: false
})
export class MaintenanceSchedulingDetailComponent implements OnInit {
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly maintenanceSchedulingDetailService = inject(MaintenanceSchedulingDetailService);
  private readonly toastHandlerService = inject(ToastHandlerService);

  @ViewChild('processMaintenanceSchedulingModal') processMaintenanceSchedulingModal?: CustomModalComponent | null = null;
  @ViewChild('generateMaintenanceOrderModal') generateMaintenanceOrderModal?: CustomModalComponent | null = null;
  @Input() disabledDetail: boolean = false;
  @Input() permissionUpdatable: boolean = false;
  @Input() maintenanceSchedulingModel: MaintenanceSchedulingModel = new MaintenanceSchedulingModel();
  @Input('typeMaintenanceScheduling') set changeType(change: number) {
    this.changeTypeMaintenanceScheduling(change);
  }
  @Output() emitQuantityDetails = new EventEmitter<number>();

  ascendant: boolean = true;
  ascendantArticle: boolean = true;
  checkboxActions: CheckBoxAction[] = [
    {
      label: 'Eliminar',
      icon: 'pi pi-trash',
      color: ActionColor.DANGER,
      action: 'bulk-delete'
    },
  ];
  clearSelectionTrigger: number = 0;
  columnSort: string | null = null;
  componentProcessMaintenanceScheduling = ProcessMaintenanceSchedulingComponent;
  componentGenerateMaintenanceOrders = GenerateMaintenanceOrdersComponent;
  detailData: PagedResponseModel<MaintenanceSchedulingDetailCaptureTableModel> =
    new PagedResponseModel<MaintenanceSchedulingDetailCaptureTableModel>();
  detailHeader: ColumnResponseModel[] = [];
  inputsForGenerateMaintenanceOrders$ = new BehaviorSubject<any>({
    maintenanceSchedulingModel: new MaintenanceSchedulingModel(),
    nameBranch: ''
  });
  inputsForProcessMaintenanceScheduling$ = new BehaviorSubject<any>({
    maintenanceSchedulingModel: new MaintenanceSchedulingModel(),
    nameBranch: ''
  });
  isFormView: boolean = false;
  limit: number = 10;
  output$ = new Subject<any>();
  page: number = 1;
  quantityDetails: number = 0;
  subscription: Subscription;
  tableActions: TableAction[] = [];

  ngOnInit() {
    this.subscription = this.output$.subscribe(({ type }) => {
      switch (type) {
        case 'onReload':
          this.getDetailsForCapture();
          break;
        default:
          console.warn('Tipo de evento desconocido:', type);
      }
    });
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    const { disabledDetail, maintenanceSchedulingModel } = changes;
    if (maintenanceSchedulingModel?.firstChange === false) {
      await this.getDetailsForCapture();
      this.clearSelectionTrigger++;
    }

    if (disabledDetail?.firstChange === false) {
      // this.tableActions = this.getDefaultActions();
    }
  }

  async changeTypeMaintenanceScheduling(type: number) {
    try {
      let response: Observable<ColumnResponseModel[]>;
      if (type == TypeMaintenanceSchedulingEnums.horometro) {
        response = this.maintenanceSchedulingDetailService.getColumnsDetailsForCapture();
      } else {
        response = this.maintenanceSchedulingDetailService.getColumnsDetailByDaysForCapture();
      }

      this.detailHeader = await firstValueFrom(response);
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta.', error);
    }
  }

  async deleteMaintenanceSchedulingDetail(selectedIds: number[]) {
    const accepted = await this.confirmDialogService.confirm('¿Realmente desea eliminar el detalle de la programación de mantenimiento?');
    if (accepted) {
      try {
        await firstValueFrom(this.maintenanceSchedulingDetailService.deleteSomeDetails(selectedIds));
        this.toastHandlerService.showToast(
          'success',
          'El detalle de la programación de mantenimiento fue eliminado exitosamente.'
        );
        this.clearSelectionTrigger++;
        await this.getDetailsForCapture();
      } catch (error) {
        console.error('Error al eliminar el detalle del medidor.', error);
      }
    }
  }

  async getDetailsForCapture() {
    try {
      const response = await firstValueFrom(
        this.maintenanceSchedulingDetailService.getDetailsForCapture(this.maintenanceSchedulingModel.maintenanceSchedulingId, this.page, this.limit, this.ascendant, this.columnSort)
      );
      this.detailData = response;
      this.quantityDetails = this.detailData.totalItems;
      this.emitQuantityDetails.emit(this.detailData.totalItems);
    } catch (error) {
      console.error('Error al obtener los detalles de la solución para la captura de datos.', error);
    }
  }

  getFormView(isFormView: boolean) {
    this.isFormView = isFormView;
  }

  async onAction(event: { action: string, item?: MaintenanceSchedulingDetailCaptureTableModel, selectedItems?: number[] }) {
    switch (event.action) {
      case 'bulk-delete': {
        if (event.selectedItems) {
          await this.deleteMaintenanceSchedulingDetail(event.selectedItems);
        }
        break;
      }
    }
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    await this.getDetailsForCapture();
  }

  processMaintenanceScheduling() {
    this.inputsForProcessMaintenanceScheduling$.next({
      maintenanceSchedulingModel: this.maintenanceSchedulingModel
    });
    this.processMaintenanceSchedulingModal?.show();
  }

  generateMaintenanceOrders() {
    this.inputsForGenerateMaintenanceOrders$.next({
      maintenanceSchedulingModel: this.maintenanceSchedulingModel
    });
    this.generateMaintenanceOrderModal?.show();
  }
}
